import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { usePageImages } from '../hooks/usePageImages';

const LuxuryFooter = () => {
  const { language } = useLanguage();
  const { getImages } = usePageImages('footer');

  const defaultFooterImages = [
    'https://images.unsplash.com/photo-1769812343628-5a2d12f297f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwZXZlbnR8ZW58MHx8fHwxNzcyMDQ4MTU1fDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1633029790372-3960e5fab043?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBmbG93ZXJzfGVufDB8fHx8MTc3MjA0ODE1OHww&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1769812343323-5e06f29e0070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwZXZlbnR8ZW58MHx8fHwxNzcyMDQ4MTU1fDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1560113406-36a33855c51e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmbG93ZXJzfGVufDB8fHx8MTc3MjA0ODE1OHww&ixlib=rb-4.1.0&q=85',
  ];

  const footerImages = getImages('gallery').length > 0 ? getImages('gallery') : defaultFooterImages;

  const footerLinks = [
    { name: language === 'es' ? 'INICIO' : 'HOME', path: '/' },
    { name: 'ABOUT', path: '/about' },
    { name: language === 'es' ? 'SERVICIOS' : 'APPROACH', path: '/evoke' },
    { name: 'STUDIO', path: '/shop' },
    { name: language === 'es' ? 'DIARIO' : 'JOURNAL', path: '/blog' },
    { name: language === 'es' ? 'CONTACTO' : 'CONTACT', path: '/contact' },
  ];

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-vainilla border-t border-stone-200">
      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Image Grid - Square Aspect Ratio */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
          {footerImages.map((img, idx) => (
            <div 
              key={idx} 
              className="aspect-square overflow-hidden rounded-xl"
            >
              <img
                src={img}
                alt={`Work ${idx + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          ))}
        </div>

        {/* Logo and Tagline - Single Instance */}
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-2">
            Margaret Núñez
          </h2>
          <p className="text-xs text-stone-500 tracking-[0.15em] uppercase">
            Founder / Creative Director
          </p>
        </div>

        {/* Instagram CTA */}
        <div className="text-center mb-10">
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-terracota text-white px-8 py-3 text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-stone-900 transition-colors duration-300"
            data-testid="footer-instagram"
          >
            {language === 'es' ? 'SÍGUEME EN INSTAGRAM' : 'FOLLOW ON INSTAGRAM'}
          </a>
        </div>

        {/* Social Icons */}
        <div className="flex justify-center gap-6 mb-10">
          <a 
            href="https://instagram.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-3 rounded-full hover:bg-champagne transition-colors"
          >
            <Instagram className="w-6 h-6 text-terracota" strokeWidth={1.5} />
          </a>
          <a 
            href="https://linkedin.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-3 rounded-full hover:bg-champagne transition-colors"
          >
            <Linkedin className="w-6 h-6 text-terracota" strokeWidth={1.5} />
          </a>
          <a 
            href="mailto:hello@margaretnunez.com" 
            className="p-3 rounded-full hover:bg-champagne transition-colors"
          >
            <Mail className="w-6 h-6 text-terracota" strokeWidth={1.5} />
          </a>
        </div>

        {/* Navigation Links */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-8 mb-10">
          {footerLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={handleLinkClick}
              className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors tracking-wider"
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Copyright */}
        <div className="text-center pt-8 border-t border-stone-200">
          <p className="text-xs text-stone-400">
            {language === 'es' 
              ? '© 2025 Margaret Núñez. Todos los derechos reservados.'
              : '© 2025 Margaret Núñez. All rights reserved.'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default LuxuryFooter;
